import { html, LitElement, type PropertyValues } from "lit";
import "./components";
import {
	DESKTOP_WALLPAPERS,
	type DesktopPreferences,
	isDesktopWallpaper,
	type RadioPreferences,
	readDesktopPreferences,
	writeDesktopPreferences,
} from "./app/desktop-preferences";
import {
	closeWindow,
	dragWindow,
	focusWindow,
	maximizeWindow,
	minimizeWindow,
	openWindow,
	reflowWindowLayout,
	resetWindowLayout,
	restoreWindow,
	restoreWindowLayout,
	serializeWindowLayout,
	type WindowLayoutState,
	type WindowViewport,
} from "./app/window-state";
import type { AppDockItem } from "./components/app-dock/app-dock";
import type { LightDomAppHost } from "./components/app-host/light-dom-app-host";
import { MetadataSync } from "./radio/metadata-sync";
import { RADIO_STATIONS, readRememberedStation } from "./radio/stations";
import { type RadioSnapshot, WebampRadioAdapter } from "./radio/webamp-adapter";
import "./styles/tokens.css";
import "./styles/reset.css";
import "./styles/desktop.css";

const WALLPAPER_LABELS = {
	blue: "Blue",
	black: "Black",
	mango: "Mango",
} as const;

const WINDOW_DEFINITIONS = [
	{
		id: "settings",
		defaultBounds: { x: 452, y: 54, width: 400, height: 400 },
		defaultMode: "closed",
		minimumSize: { width: 300, height: 400 },
		maximizable: true,
		responsivePlacement: {
			wide: { x: 0.7, y: 0.2 },
			compact: { x: 0.58, y: 0.18 },
			handheld: { x: 0.5, y: 0.22 },
		},
	},
] as const;

type DesktopWindowId = (typeof WINDOW_DEFINITIONS)[number]["id"];
type DesktopAppId = "radio" | DesktopWindowId;

function viewport(): WindowViewport {
	return {
		width: Math.max(320, window.visualViewport?.width ?? window.innerWidth),
		height: Math.max(320, window.visualViewport?.height ?? window.innerHeight),
		taskbarHeight: 84,
		gutter: 6,
	};
}

function restoreLayout(
	preferences: DesktopPreferences,
): WindowLayoutState<DesktopWindowId> {
	if (!preferences.rememberWindowPositions) {
		return restoreWindowLayout(WINDOW_DEFINITIONS, viewport(), null);
	}
	return restoreWindowLayout(
		WINDOW_DEFINITIONS,
		viewport(),
		preferences.windowLayout,
	);
}
class ZagreusDesktop extends LitElement {
	#autoplayRecoveryArmed = false;
	#dragOrigins: Partial<
		Record<DesktopWindowId, WindowLayoutState<DesktopWindowId>>
	> = {};
	#preferences = readDesktopPreferences();
	#layout = restoreLayout(this.#preferences);
	#activeAppId: DesktopAppId =
		this.#layout.windows.settings.mode !== "closed" &&
		this.#layout.windows.settings.mode !== "minimized"
			? "settings"
			: "radio";
	#metadataSync = new MetadataSync((presentation) => {
		this.#radio?.applyLivePresentation(presentation);
	});
	#radio: WebampRadioAdapter | null = null;
	#radioHidden = this.#preferences.playerHidden;
	#radioSnapshot: RadioSnapshot | null = null;
	#preferenceWriteTimer: number | null = null;
	#resizeFrame: number | null = null;
	#startingRadio = false;
	#unsubscribeRadio: (() => void) | null = null;
	#viewport = viewport();

	createRenderRoot() {
		return this;
	}

	connectedCallback(): void {
		super.connectedCallback();
		document.body.classList.add("desktop");
		window.addEventListener("resize", this.#handleResize, { passive: true });
		window.visualViewport?.addEventListener("resize", this.#handleResize, {
			passive: true,
		});
		window.addEventListener("pagehide", this.#flushPreferences);
	}

	disconnectedCallback(): void {
		window.removeEventListener("resize", this.#handleResize);
		window.visualViewport?.removeEventListener("resize", this.#handleResize);
		window.removeEventListener("pagehide", this.#flushPreferences);
		this.#flushPreferences();
		if (this.#resizeFrame !== null)
			window.cancelAnimationFrame(this.#resizeFrame);
		this.#resizeFrame = null;
		this.#metadataSync.stop();
		this.#disarmAutoplayRecovery();
		this.#unsubscribeRadio?.();
		this.#radio?.destroy();
		document.body.classList.remove("desktop");
		super.disconnectedCallback();
	}

	protected async firstUpdated(
		_changedProperties: PropertyValues,
	): Promise<void> {
		const appHost = this.querySelector<LightDomAppHost>("light-dom-app-host");
		if (!appHost) return;
		await appHost.updateComplete;
		const mount = appHost.mountElement;
		if (!mount) return;

		const initialStation = readRememberedStation(
			RADIO_STATIONS,
			this.#preferences.stationRoute,
		);
		const radio = new WebampRadioAdapter(
			RADIO_STATIONS,
			initialStation,
			this.#preferences.radio,
			this.#handleRadioPreferencesChange,
		);
		this.#radio = radio;
		let metadataRoute = "";
		this.#unsubscribeRadio = radio.subscribe((snapshot) => {
			this.#radioSnapshot = snapshot;
			if (snapshot.closed && !this.#preferences.playerHidden) {
				this.#preferences = { ...this.#preferences, playerHidden: true };
				this.#schedulePreferencesWrite();
			}
			if (snapshot.station.route !== this.#preferences.stationRoute) {
				this.#preferences = {
					...this.#preferences,
					stationRoute: snapshot.station.route,
				};
				this.#schedulePreferencesWrite();
			}
			if (snapshot.ready && snapshot.station.route !== metadataRoute) {
				metadataRoute = snapshot.station.route;
				this.#metadataSync.start(snapshot.station);
			}
			if (snapshot.playback === "playing") {
				this.#startingRadio = false;
				this.#disarmAutoplayRecovery();
			}
			if (snapshot.playback === "blocked") this.#armAutoplayRecovery();
			if (snapshot.playback === "error") {
				this.#startingRadio = false;
				this.#disarmAutoplayRecovery();
			}
			this.requestUpdate();
		});

		await radio.initialize(
			mount,
			matchMedia("(max-width: 599px), (max-height: 560px)").matches,
		);
		this.#armAutoplayRecovery();
		await radio.attemptAutoplay();
		this.requestUpdate();
	}

	render() {
		const settings = this.#layout.windows.settings;
		const settingsVisible =
			settings.mode !== "closed" && settings.mode !== "minimized";
		const radio = this.#radioSnapshot;
		const radioMinimized = this.#radioHidden || radio?.closed === true;
		const radioContinuesPlaying =
			radioMinimized && radio?.playback === "playing";
		const radioLoading = !radio || radio.playback === "loading";
		const radioNeedsAttention =
			radio?.playback === "blocked" || radio?.playback === "error";
		const radioName = radio?.station.name ?? "Zagreus Radio";
		const showMobileRadioStart =
			matchMedia("(max-width: 599px)").matches && radio?.playback === "blocked";
		const dockItems: AppDockItem[] = [
			{
				id: "radio",
				label: radioLoading
					? "Loading…"
					: radio?.playback === "blocked"
						? "Play"
						: "Player",
				title: radioLoading
					? `Connecting to ${radioName}`
					: radio?.playback === "blocked"
						? `Start ${radioName}`
						: radio?.playback === "error"
							? "Radio unavailable"
							: radioMinimized
								? `Open ${radioName}${radioContinuesPlaying ? " — playing" : ""}`
								: `Hide ${radioName}`,
				icon: "radio",
				activity: radioContinuesPlaying ? "playing" : undefined,
				active:
					!radioMinimized &&
					radio?.playback !== "error" &&
					this.#activeAppId === "radio",
				attention: radioNeedsAttention,
			},
			{
				id: "settings",
				label: "Settings",
				title: settingsVisible
					? this.#activeAppId === "settings"
						? "Minimize Settings"
						: "Activate Settings"
					: "Open Settings",
				icon: "settings",
				active: this.#activeAppId === "settings" && settingsVisible,
			},
		];

		return html`
      <desktop-shell
        .title=${"Zagreus desktop"}
        wallpaper=${this.#preferences.wallpaper}
      >
        <light-dom-app-host
          slot="windows"
          class="webamp-app-host"
          accessible-label="Zagreus Radio in Webamp"
          .busy=${radioLoading}
          style=${`z-index:${this.#activeAppId === "radio" ? 30 : 10}`}
          ?hidden=${radioMinimized}
          @pointerdown=${this.#focusRadio}
        ></light-dom-app-host>
        ${
					settingsVisible
						? html`
              <window-frame
                slot="windows"
                class="settings-window"
                window-id="settings"
                title="Settings"
                icon="settings"
                .active=${this.#activeAppId === "settings"}
                .maximized=${settings.mode === "maximized"}
                style=${`left:${settings.bounds.x}px;top:${settings.bounds.y}px;width:${settings.bounds.width}px;height:${settings.bounds.height}px;z-index:${this.#activeAppId === "settings" ? 30 : 20}`}
                @window-focus-request=${() => this.#focusShellWindow("settings")}
                @window-drag-start=${() => this.#startShellWindowDrag("settings")}
                @window-drag-move=${(
									event: CustomEvent<{ deltaX: number; deltaY: number }>,
								) => this.#moveShellWindow("settings", event)}
                @window-toggle-minimize=${() => this.#minimizeShellWindow("settings")}
                @window-toggle-maximize=${() => this.#toggleMaximizeShellWindow("settings")}
                @window-close-request=${() => this.#closeShellWindow("settings")}
              >
                <div slot="app" class="settings-panel">
                  <form
                    class="settings-content"
                    @submit=${(event: SubmitEvent) => event.preventDefault()}
                  >
                    <fieldset>
                      <legend>Desktop</legend>
                      <div class="wallpaper-setting">
                        <span class="setting-label" id="wallpaper-label">Wallpaper</span>
                        <div
                          class="wallpaper-options"
                          role="radiogroup"
                          aria-labelledby="wallpaper-label"
                        >
                          ${DESKTOP_WALLPAPERS.map(
														(wallpaper) => html`
                              <label class="wallpaper-option">
                                <input
                                  type="radio"
                                  name="wallpaper"
                                  value=${wallpaper}
                                  .checked=${this.#preferences.wallpaper === wallpaper}
                                  @change=${this.#handleWallpaperChange}
                                  @keydown=${this.#handleWallpaperKeyDown}
                                />
                                <span
                                  class="wallpaper-thumbnail wallpaper-thumbnail--${wallpaper}"
                                  aria-hidden="true"
                                ></span>
                                <span>${WALLPAPER_LABELS[wallpaper]}</span>
                              </label>
                            `,
													)}
                        </div>
                      </div>
                      <label class="classic-checkbox">
                        <input
                          type="checkbox"
                          .checked=${this.#preferences.rememberWindowPositions}
                          @change=${this.#handleRememberLayoutChange}
                        />
                        <span>Remember window positions</span>
                      </label>
                      <button type="button" @click=${this.#resetDesktopLayout}>
                        Reset window positions
                      </button>
                    </fieldset>
                    <fieldset>
                      <legend>Radio</legend>
                      <div class="setting-row">
                        <span>Webamp player</span>
                        <button type="button" @click=${this.#toggleRadioVisibility}>
                          ${radioMinimized ? "Show player" : "Hide player"}
                        </button>
                      </div>
                      <p>Hiding the player does not stop the live station.</p>
                    </fieldset>
                  </form>
                  <settings-footer></settings-footer>
                </div>
              </window-frame>
            `
						: null
				}
        ${
					showMobileRadioStart
						? html`
              <button
                slot="overlay"
                class="mobile-radio-start"
                data-radio-start
                type="button"
                ?disabled=${this.#startingRadio}
                @click=${this.#startRadioFromGesture}
              >
                ${this.#startingRadio ? "Starting…" : "Play radio"}
              </button>
            `
						: null
				}
        ${
					radio?.playback === "error"
						? html`
              <window-frame
                slot="overlay"
                class="radio-error-dialog"
                window-id="start-radio"
                title="Radio unavailable"
                icon="radio"
                .closable=${false}
                .minimizable=${false}
                .maximizable=${false}
                .draggable=${false}
              >
                <div slot="app" class="radio-error-content">
                  <p>${radio.error}</p>
                  <button type="button" @click=${this.#reloadDesktop}>Reload desktop</button>
                </div>
              </window-frame>
            `
						: null
				}
        <app-dock
          slot="dock"
          .items=${dockItems}
          @dock-item-activate=${this.#handleDockActivate}
        ></app-dock>
      </desktop-shell>
    `;
	}

	#showRadio = () => {
		if (this.#radioSnapshot?.closed) this.#radio?.reopen();
		this.#radioHidden = false;
		this.#preferences = { ...this.#preferences, playerHidden: false };
		this.#schedulePreferencesWrite();
		this.#activeAppId = "radio";
		this.requestUpdate();
		window.requestAnimationFrame(() =>
			this.#radio?.reflow(
				matchMedia("(max-width: 599px), (max-height: 560px)").matches,
			),
		);
	};

	#reloadDesktop = () => {
		window.location.reload();
	};

	#openShellWindow(windowId: DesktopWindowId) {
		this.#layout = openWindow(
			this.#layout,
			WINDOW_DEFINITIONS,
			viewport(),
			windowId,
		);
		this.#activeAppId = windowId;
		this.#persistLayout();
	}

	#focusShellWindow(windowId: DesktopWindowId) {
		this.#layout = focusWindow(this.#layout, windowId);
		this.#activeAppId = windowId;
		this.#persistLayout();
	}

	#startShellWindowDrag(windowId: DesktopWindowId) {
		this.#dragOrigins[windowId] = this.#layout;
	}

	#moveShellWindow(
		windowId: DesktopWindowId,
		event: CustomEvent<{ deltaX: number; deltaY: number }>,
	) {
		const origin = this.#dragOrigins[windowId];
		if (!origin) return;
		this.#layout = dragWindow(
			origin,
			WINDOW_DEFINITIONS,
			viewport(),
			windowId,
			{
				dx: event.detail.deltaX,
				dy: event.detail.deltaY,
			},
		);
		this.#persistLayout();
	}

	#minimizeShellWindow(windowId: DesktopWindowId) {
		this.#layout = minimizeWindow(
			this.#layout,
			WINDOW_DEFINITIONS,
			viewport(),
			windowId,
		);
		this.#activeAppId = "radio";
		this.#persistLayout();
	}

	#toggleMaximizeShellWindow(windowId: DesktopWindowId) {
		this.#layout =
			this.#layout.windows[windowId].mode === "maximized"
				? restoreWindow(this.#layout, WINDOW_DEFINITIONS, viewport(), windowId)
				: maximizeWindow(
						this.#layout,
						WINDOW_DEFINITIONS,
						viewport(),
						windowId,
					);
		this.#activeAppId = windowId;
		this.#persistLayout();
	}

	#closeShellWindow(windowId: DesktopWindowId) {
		this.#layout = closeWindow(
			this.#layout,
			WINDOW_DEFINITIONS,
			viewport(),
			windowId,
		);
		this.#activeAppId = "radio";
		this.#persistLayout();
	}

	#toggleShellWindowFromDock(windowId: DesktopWindowId) {
		const mode = this.#layout.windows[windowId].mode;
		if (mode === "closed") {
			this.#openShellWindow(windowId);
			return;
		}
		if (mode === "minimized") {
			this.#layout = restoreWindow(
				this.#layout,
				WINDOW_DEFINITIONS,
				viewport(),
				windowId,
			);
			this.#activeAppId = windowId;
		} else if (this.#activeAppId === windowId) {
			this.#layout = minimizeWindow(
				this.#layout,
				WINDOW_DEFINITIONS,
				viewport(),
				windowId,
			);
			this.#activeAppId = "radio";
		} else {
			this.#layout = focusWindow(this.#layout, windowId);
			this.#activeAppId = windowId;
		}
		this.#persistLayout();
	}

	#handleDockActivate = (event: CustomEvent<{ itemId: string }>) => {
		if (event.detail.itemId === "radio") {
			if (this.#radioSnapshot?.playback === "blocked") {
				this.#showRadio();
				this.#startRadioFromGesture();
			} else if (this.#radioHidden || this.#radioSnapshot?.closed) {
				this.#showRadio();
			} else if (this.#activeAppId !== "radio") {
				this.#focusRadio();
			} else {
				this.#radioHidden = true;
				this.#preferences = { ...this.#preferences, playerHidden: true };
				this.#schedulePreferencesWrite();
			}
		}
		if (event.detail.itemId === "settings")
			this.#toggleShellWindowFromDock("settings");
		this.requestUpdate();
	};

	#handleRememberLayoutChange = (event: Event) => {
		if (!(event.currentTarget instanceof HTMLInputElement)) return;
		this.#preferences = {
			...this.#preferences,
			rememberWindowPositions: event.currentTarget.checked,
			windowLayout: event.currentTarget.checked
				? this.#preferences.windowLayout
				: null,
		};
		this.#persistLayout();
	};

	#handleWallpaperChange = (event: Event) => {
		if (!(event.currentTarget instanceof HTMLInputElement)) return;
		if (!isDesktopWallpaper(event.currentTarget.value)) return;
		this.#selectWallpaper(event.currentTarget.value);
	};

	#handleWallpaperKeyDown = (event: KeyboardEvent) => {
		if (!(event.currentTarget instanceof HTMLInputElement)) return;
		if (!isDesktopWallpaper(event.currentTarget.value)) return;
		const currentIndex = DESKTOP_WALLPAPERS.indexOf(event.currentTarget.value);
		let nextIndex = currentIndex;
		if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
			nextIndex =
				(currentIndex - 1 + DESKTOP_WALLPAPERS.length) %
				DESKTOP_WALLPAPERS.length;
		} else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
			nextIndex = (currentIndex + 1) % DESKTOP_WALLPAPERS.length;
		} else if (event.key === "Home") {
			nextIndex = 0;
		} else if (event.key === "End") {
			nextIndex = DESKTOP_WALLPAPERS.length - 1;
		} else if (event.key !== " " && event.key !== "Enter") {
			return;
		}
		event.preventDefault();
		const wallpaper = DESKTOP_WALLPAPERS[nextIndex];
		if (!wallpaper) return;
		this.#selectWallpaper(wallpaper);
		void this.updateComplete.then(() => {
			this.querySelector<HTMLInputElement>(
				`input[name="wallpaper"][value="${wallpaper}"]`,
			)?.focus();
		});
	};

	#selectWallpaper(wallpaper: DesktopPreferences["wallpaper"]) {
		this.#preferences = {
			...this.#preferences,
			wallpaper,
		};
		this.#schedulePreferencesWrite();
		this.requestUpdate();
	}

	#handleRadioPreferencesChange = (radio: RadioPreferences) => {
		this.#preferences = { ...this.#preferences, radio };
		this.#schedulePreferencesWrite();
	};

	#toggleRadioVisibility = () => {
		if (this.#radioHidden || this.#radioSnapshot?.closed) this.#showRadio();
		else {
			this.#radioHidden = true;
			this.#preferences = { ...this.#preferences, playerHidden: true };
			this.#schedulePreferencesWrite();
			this.requestUpdate();
		}
	};

	#resetDesktopLayout = () => {
		const reset = resetWindowLayout(WINDOW_DEFINITIONS, viewport());
		this.#layout = openWindow(
			reset,
			WINDOW_DEFINITIONS,
			viewport(),
			"settings",
		);
		this.#activeAppId = "settings";
		this.#persistLayout();
	};

	#startRadioFromGesture = () => {
		this.#disarmAutoplayRecovery();
		this.#activeAppId = "radio";
		this.#startingRadio = true;
		this.#radio?.playLive();
		window.setTimeout(() => {
			if (this.#radioSnapshot?.playback !== "playing") {
				this.#startingRadio = false;
				if (this.#radioSnapshot?.playback === "blocked")
					this.#armAutoplayRecovery();
			}
			this.requestUpdate();
		}, 1_200);
		this.requestUpdate();
	};

	#focusRadio = () => {
		if (this.#radioHidden || this.#radioSnapshot?.closed) return;
		if (this.#activeAppId === "radio") return;
		this.#activeAppId = "radio";
		this.requestUpdate();
	};

	#armAutoplayRecovery() {
		if (this.#autoplayRecoveryArmed) return;
		this.#autoplayRecoveryArmed = true;
		document.addEventListener(
			"pointerdown",
			this.#recoverAutoplayOnGesture,
			true,
		);
		document.addEventListener("keydown", this.#recoverAutoplayOnGesture, true);
	}

	#disarmAutoplayRecovery() {
		if (!this.#autoplayRecoveryArmed) return;
		this.#autoplayRecoveryArmed = false;
		document.removeEventListener(
			"pointerdown",
			this.#recoverAutoplayOnGesture,
			true,
		);
		document.removeEventListener(
			"keydown",
			this.#recoverAutoplayOnGesture,
			true,
		);
	}

	#recoverAutoplayOnGesture = (event: Event) => {
		if (
			event instanceof KeyboardEvent &&
			event.key !== "Enter" &&
			event.key !== " "
		)
			return;
		if (event instanceof PointerEvent && event.button !== 0) return;
		if (
			event.target instanceof Element &&
			event.target.closest("[data-radio-start], #play")
		)
			return;
		this.#startRadioFromGesture();
	};

	#handleResize = () => {
		if (this.#resizeFrame !== null) return;
		this.#resizeFrame = window.requestAnimationFrame(() => {
			this.#resizeFrame = null;
			const nextViewport = viewport();
			const previousCompact =
				this.#viewport.width < 600 || this.#viewport.height < 560;
			const nextCompact = nextViewport.width < 600 || nextViewport.height < 560;
			const widthChanged = nextViewport.width !== this.#viewport.width;
			const meaningfulHeightChange =
				Math.abs(nextViewport.height - this.#viewport.height) >= 72;
			if (
				!widthChanged &&
				!meaningfulHeightChange &&
				previousCompact === nextCompact
			)
				return;
			this.#viewport = nextViewport;
			this.#layout = reflowWindowLayout(
				this.#layout,
				WINDOW_DEFINITIONS,
				nextViewport,
			);
			this.#radio?.reflow(nextCompact);
			this.#persistLayout();
		});
	};

	#persistLayout(): void {
		this.#preferences = {
			...this.#preferences,
			windowLayout: this.#preferences.rememberWindowPositions
				? serializeWindowLayout(this.#layout)
				: null,
		};
		this.#schedulePreferencesWrite();
		this.requestUpdate();
	}

	#schedulePreferencesWrite(): void {
		if (this.#preferenceWriteTimer !== null) {
			window.clearTimeout(this.#preferenceWriteTimer);
		}
		this.#preferenceWriteTimer = window.setTimeout(this.#flushPreferences, 200);
	}

	#flushPreferences = (): void => {
		if (this.#preferenceWriteTimer === null) return;
		window.clearTimeout(this.#preferenceWriteTimer);
		this.#preferenceWriteTimer = null;
		try {
			writeDesktopPreferences(this.#preferences);
		} catch {
			// Preferences remain available for the current visit.
		}
	};
}

if (!customElements.get("zagreus-desktop")) {
	customElements.define("zagreus-desktop", ZagreusDesktop);
}
